import { useEffect, useMemo, useRef, useState } from 'react'
import { api, ApiError, type CallDraft } from '../lib/api'
import { AudiencePicker, reachesNobody, useAudiences } from './AudiencePicker'
import { draftMissing, openBody, previewBody, type IntakeDraft } from './Intake'

/** BROADCAST DRAFTS: SEVERAL NEW CALLS, ONE MESSAGE (074).
 *
 *  The customer, 10 Oct 2026: four calls come in, "I take the calls fast, I
 *  put them on draft standby ... And the members are receiving three, four,
 *  five text messages or emails, and they only look at the last one." Every
 *  draft is listed, oldest first; the ticked ones are OPENED AS CALLS exactly
 *  as "Create and broadcast" would open them (`openBody`, the one
 *  definition), and then ONE message carries each call's own first broadcast
 *  (his Q1 A). A draft left unticked stays a draft -- "maybe some of them is
 *  a real draft".
 *
 *  NOTHING IS OPENED THAT WILL NOT BE SENT: the server is asked first whether
 *  the message would be refused (nobody on duty, too long), and a refusal
 *  stops the press with every draft still a draft. A draft that cannot open
 *  is shown, not tickable: one missing its nature or its address (what the
 *  server refuses), one a colleague has open, one saved by an older screen.
 *  WHAT WAS SEEN IS WHAT GOES: every ticked draft is read again before
 *  anything is opened, and one that changed stops the press. A call this
 *  window opened STAYS IN IT, ticked and fixed, until it is sent -- a second
 *  press sends it, never opens it twice, and closing the window names it.
 *  Every class carries `bd-` (the frame is the Rebroadcast window's `cb-`). */

function newKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

/** As `combined_rebroadcast.MAX_CALLS`. */
const MAX_CALLS = 20

type Row = {
  card: CallDraft
  data: IntakeDraft
  /** Why it cannot be ticked, or null. */
  why: string | null
  on: boolean
}

type Preview = { whole: string; characters: number; problem: string | null }

const label = (r: Row) => r.card.nature_label || 'A draft'

export function BroadcastDrafts({ onClose, onDone }: {
  /** Closed; `note` names calls this window opened and did not send. */
  onClose: (note?: string | null) => void
  /** After the press: what happened, in words. */
  onDone: (said: string) => void
}) {
  const [rows, setRows] = useState<Row[] | null>(null)
  const [audience, setAudience] = useState<string[]>([])
  const audiences = useAudiences()
  const [preview, setPreview] = useState<Preview | null>(null)
  const [previewNote, setPreviewNote] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [sending, setSending] = useState(false)
  /** ONE PRESS, ONE MESSAGE -- for one set of calls: renewed when the set the
   *  server is asked to send changes. */
  const key = useRef(newKey())
  const lastIds = useRef('')
  /** Drafts this window already opened, draft id -> call id: a second press
   *  sends their calls, never opens them twice. */
  const opened = useRef(new Map<string, string>())

  async function load() {
    setError(null)
    try {
      const cards = await api.callDrafts()
      const full = await Promise.all(cards.map((c) => api.callDraftOne(c.id).catch(() => null)))
      const fresh: Row[] = cards.flatMap((_card, i) => {
        const one = full[i]
        if (!one || opened.current.has(one.id)) return []
        const data = one.data as unknown as IntakeDraft
        const held = one.held_by_label && !one.held_by_me ? `${one.held_by_label} has it open` : null
        const why = data?.v !== 1 ? 'Saved by an older screen — open it to finish'
          : draftMissing(data) ?? held
        return [{ card: one, data, why, on: !why }]
      })
      // A CALL THIS WINDOW OPENED IS NO LONGER A DRAFT, so the list no
      // longer has it: kept from before, or it would drop out unsent.
      setRows((prev) => [...(prev ?? []).filter((r) => opened.current.has(r.card.id)), ...fresh]
        // OLDEST FIRST, as the queue: the first caller is "1)".
        .sort((a, b) => a.card.created_at.localeCompare(b.card.created_at)))
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not read the drafts.')
      setRows((prev) => prev ?? [])
    }
  }
  useEffect(() => { load() }, [])

  const ticked = useMemo(() => (rows ?? []).filter((r) => opened.current.has(r.card.id) || (r.on && !r.why)), [rows])
  const tickedKey = ticked.map((r) => r.card.id).join(',')

  // WHAT THEY WILL RECEIVE, and whether it would be refused, from the server.
  useEffect(() => {
    if (!ticked.length || ticked.length > MAX_CALLS) { setPreview(null); return }
    let alive = true
    const t = setTimeout(() => {
      api.draftsPreview(ticked.map((r) => previewBody(r.data)), audience)
        .then((p) => { if (alive) { setPreview(p); setPreviewNote(null) } })
        .catch((e) => { if (alive) { setPreview(null); setPreviewNote(e instanceof ApiError ? e.message : 'Could not show the message.') } })
    }, 250)
    return () => { alive = false; clearTimeout(t) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickedKey, audience.join(',')])

  /** Calls this window opened and has not sent, by name. */
  const unsent = () => (rows ?? []).filter((r) => opened.current.has(r.card.id)).map(label)
  const close = () => {
    const left = unsent()
    onClose(left.length
      ? `${left.join(', ')} ${left.length === 1 ? 'is' : 'are'} open but not broadcast — press Broadcast on ${left.length === 1 ? 'the call' : 'each call'}.`
      : null)
  }

  // ESCAPE CLOSES IT, as every dialog here; never while it is sending.
  const dialog = useRef<HTMLDivElement>(null)
  useEffect(() => { dialog.current?.focus() }, [])
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !sending) close() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  async function send() {
    if (sending || !ticked.length) return
    setSending(true)
    setError(null)
    try {
      // 0. WOULD IT BE REFUSED? Asked now, with the audience chosen, before
      // a single draft becomes a call.
      const check = await api.draftsPreview(ticked.map((r) => previewBody(r.data)), audience)
      // Said, and the button left as it was: pressing again asks again, so a
      // member coming on duty is all it takes.
      if (check.problem) {
        setError(check.problem)
        return
      }
      // 1. WHAT WAS SEEN IS WHAT GOES: each ticked draft read again. One that
      // changed, was opened elsewhere or is now in a colleague's hands stops
      // the press -- the window is refreshed and nothing more is opened.
      const waiting = ticked.filter((r) => !opened.current.has(r.card.id))
      const fresh = await Promise.all(waiting.map((r) => api.callDraftOne(r.card.id).catch(() => null)))
      const moved = waiting.filter((r, i) => {
        const f = fresh[i]
        return !f || f.status !== 'open' || f.version !== r.card.version
          || (!!f.held_by_label && !f.held_by_me)
      })
      if (moved.length) {
        setError(`${moved.length === 1 ? 'A draft' : `${moved.length} drafts`} changed since this window opened. `
          + 'Check the message and press again.')
        await load()
        return
      }
      // 2. EACH OPENED AS A CALL, in the order shown, exactly as "Create and
      // broadcast" opens it. One that will not open is named; the others go.
      const trouble: string[] = []
      for (const r of waiting) {
        try {
          const created = await api.incidentOpen(openBody(r.data, r.card.id))
          opened.current.set(r.card.id, created.id)
          // WHAT DID NOT STICK, as the form says it.
          const said = [created.answers?.refused, ...Object.values(created.answers?.notes ?? {}),
            created.answers?.dropped?.length
              ? `${created.answers.dropped.length} answer(s) were not stored — answer them again on the call.`
              : null,
            created.numbers?.note, created.notes?.note].filter(Boolean)
          if (said.length) trouble.push(`${label(r)}: ${said.join(' ')}`)
        } catch (e) {
          trouble.push(`${label(r)} was not opened — ${e instanceof ApiError ? e.message : 'no answer from the server'}.`)
        }
      }
      setRows((rs) => rs && [...rs])   // the opened ones now show as such
      const ids = ticked.map((r) => opened.current.get(r.card.id)).filter((x): x is string => !!x)
      if (!ids.length) {
        setError(trouble.join(' '))
        return
      }
      // 3. ONE MESSAGE for every call that opened -- a new key for a new set.
      const idsKey = ids.join(',')
      if (idsKey !== lastIds.current) { key.current = newKey(); lastIds.current = idsKey }
      try {
        const out = await api.draftsSend({ incident_ids: ids, group_ids: audience, request_key: key.current })
        const n = out.calls ?? ids.length
        const skipped = out.skipped?.length
          ? ` ${out.skipped.join(', ')} had already been broadcast, so ${out.skipped.length === 1 ? 'it was' : 'they were'} left out.`
          : ''
        opened.current.clear()
        onDone(`${n} call${n === 1 ? '' : 's'} broadcast in one message to `
          + `${out.recipients ?? 0} member${out.recipients === 1 ? '' : 's'}.${skipped}`
          + (trouble.length ? ` ${trouble.join(' ')}` : ''))
      } catch (e) {
        // THE CALLS ARE OPEN; THE MESSAGE IS WHAT FAILED. Said plainly, with
        // the way out -- pressing again sends them, never opens them twice.
        setError(`${ids.length} call${ids.length === 1 ? ' is' : 's are'} open but the message did not go out — `
          + `${e instanceof ApiError ? e.message : 'no answer from the server'} Press Send again, `
          + 'or Broadcast on each call.' + (trouble.length ? ` ${trouble.join(' ')}` : ''))
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not check the message. Nothing was opened.')
    } finally {
      setSending(false)
    }
  }

  const set = (id: string, on: boolean) =>
    setRows((rs) => (rs ?? []).map((r) => (r.card.id === id ? { ...r, on } : r)))

  const nobody = audience.length > 0 && reachesNobody(audiences, audience)
  const left = (rows ?? []).filter((r) => !opened.current.has(r.card.id)).length
    - ticked.filter((r) => !opened.current.has(r.card.id)).length
  const why = !ticked.length ? 'Tick at least one draft.'
    : ticked.length > MAX_CALLS ? `${MAX_CALLS} calls at most in one message — untick some.`
    : nobody ? 'Nobody on duty in the groups you chose.'
    : preview?.problem ?? null

  return (
    <div className="cb-bg" role="dialog" aria-modal="true" aria-labelledby="bd-h">
      <div className="cb-modal" ref={dialog} tabIndex={-1}>
        <div className="cb-head">
          <h2 id="bd-h">Broadcast drafts — one message</h2>
          <button type="button" className="btn sm" onClick={close} disabled={sending} aria-label="Close">✕</button>
        </div>
        {rows === null ? (
          <div className="cb-body1">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="cb-body1">{error ?? 'No drafts are waiting.'}</div>
        ) : (
          <div className="cb-body">
            <div className="cb-calls">
              <div className="cb-lab">1. Tick the drafts to open and send. Each one becomes a call.</div>
              {rows.map((r) => {
                const isOpen = opened.current.has(r.card.id)
                return (
                  <div key={r.card.id} className={`bd-draft ${isOpen ? 'bd-opened' : r.why ? 'bd-cant' : r.on ? '' : 'bd-off'}`}>
                    <label className="cb-ct">
                      <input type="checkbox" checked={isOpen || (r.on && !r.why)}
                             disabled={isOpen || !!r.why || sending}
                             onChange={(e) => set(r.card.id, e.target.checked)} />
                      <b>{r.card.nature_label || 'No nature yet'}</b>
                      <small>{r.card.caller_label ?? ''}</small>
                    </label>
                    <div className="cb-where">
                      {r.card.location_label || (r.data.locDetails ?? '').trim() || 'No address yet'}
                    </div>
                    {isOpen ? <div className="bd-isopen">Opened as a call — goes with this message</div>
                      : r.why && <div className="bd-why">{r.why}</div>}
                  </div>
                )
              })}
            </div>
            <div className="cb-side">
              <div className="cb-lab">2. Send to</div>
              <AudiencePicker audiences={audiences} value={audience} onChange={setAudience} disabled={sending} />
              <div className="cb-lab" style={{ marginTop: 12 }}>3. What they will receive</div>
              <pre className="cb-preview">{preview?.whole ?? previewNote ?? (why ?? '…')}</pre>
              {preview && (
                <div className={`cb-count ${preview.problem ? 'cb-over' : ''}`}>
                  {preview.characters} characters{preview.problem ? ` — ${preview.problem}` : ''}
                </div>
              )}
            </div>
          </div>
        )}
        {error && rows !== null && rows.length > 0 && <div className="notice cb-err">{error}</div>}
        <div className="cb-foot">
          <span className="cb-hint">{why ?? `${left} draft${left === 1 ? ' stays a draft' : 's stay drafts'}`}</span>
          <button type="button" className="btn" onClick={close} disabled={sending}>Cancel</button>
          <button type="button" className="btn primary" disabled={!!why || sending || !rows?.length} onClick={send}>
            {sending ? 'Sending…' : `Open and broadcast ${ticked.length} call${ticked.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  )
}
